/**
 * Normalizes a WhatsApp phone number for wa.me links.
 *
 * Rules:
 * 1. Strip all non-digit characters (+, spaces, parentheses, dashes, dots)
 * 2. Brazil: starts with 55, 12-13 digits → use as is
 * 3. US with country code: 11 digits, starts with 1 → use as is
 * 4. US without country code: exactly 10 digits → prefix 1
 * 5. Anything else (< 10 digits or empty) → return null
 *
 * Never duplicates the country code.
 */
export function normalizeWhatsapp(whatsapp: string, countryCode?: string): string | null {
  const rawDigits = (whatsapp || '').replace(/\D/g, '');
  if (!rawDigits || rawDigits.length < 10) return null;

  const ccDigits = (countryCode || '').replace(/\D/g, '');

  // Avoid duplicating country code: if raw already starts with country digits, use raw as-is
  let digits: string;
  if (ccDigits && rawDigits.startsWith(ccDigits)) {
    digits = rawDigits;
  } else {
    digits = ccDigits + rawDigits;
  }

  // Rule 2: Brazil — 55 + 10-11 = 12-13 digits
  if (digits.startsWith('55') && (digits.length === 12 || digits.length === 13)) {
    return digits;
  }

  // Rule 3: US with country code — 1 + 10 = 11 digits
  if (digits.length === 11 && digits.startsWith('1')) {
    return digits;
  }

  // Rule 4: US without country code — exactly 10 digits
  if (digits.length === 10) {
    return '1' + digits;
  }

  // Rule 5: anything else
  return null;
}

/**
 * Given a normalized phone number, returns the full wa.me URL.
 * Returns null if the number is invalid.
 */
export function getWhatsappUrl(whatsapp: string, countryCode?: string): string | null {
  const normalized = normalizeWhatsapp(whatsapp, countryCode);
  if (!normalized) return null;
  return `https://wa.me/${normalized}`;
}